import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { RoomCommand, RoomProjection, Submission } from "@quizshow/protocol";
import { AnswerMarkControl } from "../components/answer-mark-control";
import { QuestionToolbar } from "../components/question-toolbar";
import { Brand } from "../components/brand";
import { FeedbackButton } from "../components/feedback";
import { BonusRewardStatus, JokerStatus, LivesStatus, ScoreStatus } from "../components/game-status";
import { RoomImage, SharedAudio } from "../components/media";
import { QuizBoard } from "../components/quiz-board";
import { ConnectionStatus, ErrorBanner } from "../components/status";
import { Timer } from "../components/timer";
import { TeamColorControl, TeamControlCard } from "../components/team-control-card";
import { useRoomConnection } from "../hooks/use-room-connection";
import { loadSession } from "../session";
import { prepareMediaFile } from "../media";
import { uploadRoomMedia } from "../api";
import { answerPresentationClasses, useFiftyFiftyComplete } from "../question-presentation";

function submissionText(projection: RoomProjection, submission: Submission): string {
  if (submission.answerMode === "free-text") return submission.text ?? "";
  return submission.optionIds
    .map((optionId) => projection.question?.answers.find((answer) => answer.optionId === optionId)?.text ?? optionId)
    .join(", ");
}

const evaluationLabels = {
  pending: "Ungeprüft",
  correct: "Richtig",
  incorrect: "Falsch",
  partial: "Teilweise/Manuell",
} as const;

export function HostPage({ role = "host" }: { role?: "host" | "cohost" }) {
  const { roomId } = useParams();
  const session = roomId ? loadSession(roomId, role) : undefined;
  const { projection, status, error, setError, sendCommand } = useRoomConnection(roomId, session?.token);
  const [teamName, setTeamName] = useState("");
  const [teamColor, setTeamColor] = useState("#ef4565");
  const [teamLogo, setTeamLogo] = useState<File>();
  const [jokerTargets, setJokerTargets] = useState<Record<string, string>>({});
  const fiftyFiftyComplete = useFiftyFiftyComplete(projection?.fiftyFiftyAnimation);

  const buzzerWinner = useMemo(
    () => projection?.players.find((player) => player.playerId === projection.buzzer.winner?.playerId),
    [projection],
  );
  const manualScoreTargets = projection
    ? [
        ...projection.teams.map((team) => ({ type: "team" as const, id: team.teamId, label: team.name })),
        ...projection.players
          .filter((player) => !player.teamId)
          .map((player) => ({ type: "player" as const, id: player.playerId, label: player.displayName })),
      ]
    : [];

  if (!session) {
    return (
      <main className="shell">
        <Brand />
        <ErrorBanner message="Keine gültige Host- oder Co-Host-Sitzung gefunden." />
        <Link className="button button--primary" to="/">
          Zur Startseite
        </Link>
      </main>
    );
  }

  const command = (value: RoomCommand) => sendCommand(value);
  const scoreStep = projection?.question?.points || 100;
  const scoringStep = projection?.scoringMode === "rank-progress" ? 1 : scoreStep;
  const adjustScoring = (target: "player" | "team", targetId: string, direction: -1 | 1) => {
    if (projection?.scoringMode === "rank-progress") {
      command({ type: "ADD_RANK_PROGRESS", target, targetId, delta: direction });
    } else {
      command({ type: "ADD_SCORE", target, targetId, delta: direction * scoreStep });
    }
  };
  const createTeam = async () => {
    if (!roomId || !teamName.trim()) return;
    try {
      let logoMediaId: string | undefined;
      if (teamLogo) {
        const prepared = await prepareMediaFile(teamLogo, "icon");
        await uploadRoomMedia(roomId, session.token, prepared.descriptor.mediaId, prepared.blob);
        logoMediaId = prepared.descriptor.mediaId;
      }
      command({ type: "CREATE_TEAM", name: teamName, color: teamColor, logoMediaId });
      setTeamName("");
      setTeamLogo(undefined);
    } catch (teamError) {
      setError(teamError instanceof Error ? teamError.message : "Team konnte nicht erstellt werden.");
    }
  };
  const resetQuestion = () => {
    if (!window.confirm("Aktuelle Frage wirklich zurücksetzen? Antworten, Markierungen, Timer, Buzzer und 50:50 werden gelöscht.")) {
      return;
    }
    const revertEffects = window.confirm(
      "Auch Wertungsänderungen seit Fragenstart zurücknehmen? OK = ja, Abbrechen = nur die Frage zurücksetzen.",
    );
    command({ type: "RESET_QUESTION", revertEffects });
  };
  const previousQuestion = () => {
    const hasEvaluation =
      projection?.questionResolution.resolved ||
      Boolean(projection?.answerMarks.length) ||
      Boolean(projection?.submissions?.some((submission) => submission.evaluation !== "pending"));
    if (hasEvaluation && !window.confirm("Zur vorherigen Frage wechseln?")) return;
    command({ type: "PREVIOUS_QUESTION" });
  };

  return (
    <main className="shell shell--wide">
      <Brand />
      <div className="control-topbar">
        <div>
          <span className="eyebrow">{role === "host" ? "Host Control" : "Co-Host Control"}</span>
          <h1>{projection?.quizTitle ?? "Raum wird geladen..."}</h1>
        </div>
        <div className="topbar-status">
          <FeedbackButton audience="host" roomId={roomId} mode={projection?.moduleId ?? "classic"} />
          <ConnectionStatus status={status} />
          {projection?.roomCode ? <span className="room-code">{projection.roomCode}</span> : null}
        </div>
      </div>
      <ErrorBanner message={error} onClose={() => setError(undefined)} />

      {role === "host" && session.streamUrl ? (
        <section className="card link-card">
          <div>
            <strong>Geschützter Stream-Link</strong>
            <code>{session.streamUrl}</code>
          </div>
          <div>
            <strong>Co-Host-Link</strong>
            <code>{session.cohostUrl}</code>
          </div>
        </section>
      ) : null}

      {!projection ? (
        <div className="card empty-state">Live-Zustand wird synchronisiert...</div>
      ) : (
        <>
          <SharedAudio
            roomId={roomId}
            token={session.token}
            mediaId={projection.audio.mediaId}
            playing={projection.audio.status === "playing"}
          />
          <div className="control-layout">
            <section className="control-main">
              <article
                className="card stage-card question-stage question-stage--host"
                style={{ backgroundColor: projection.question?.media.backgroundColor }}
              >
                {projection.question?.media.backgroundMediaId ? (
                  <RoomImage
                    roomId={roomId}
                    token={session.token}
                    mediaId={projection.question.media.backgroundMediaId}
                    className="question-background"
                  />
                ) : null}
                <div className="stage-card__content">
                  <div className="stage-card__header">
                    <span className="badge">{projection.moduleId}</span>
                    {projection.question?.timerEnabled || projection.timer.status === "running" ? (
                      <Timer timer={projection.timer} />
                    ) : null}
                  </div>
                  {projection.status === "ended" ? (
                    <EndScreen projection={projection} />
                  ) : projection.moduleId === "quizboard" && projection.board && !projection.question ? (
                    <QuizBoard board={projection.board} onOpen={(cellId) => command({ type: "OPEN_BOARD_CELL", cellId })} />
                  ) : projection.question ? (
                    <>
                      <span className="eyebrow">
                        {projection.moduleId === "quizboard"
                          ? `${projection.question.points} Punkte`
                          : `Frage ${projection.currentQuestionIndex + 1} von ${projection.totalQuestions}`}
                      </span>
                      {projection.question.questionType === "bonus" ? <span className="badge">Bonusfrage</span> : null}
                      {projection.activePointMultiplier === 2 ? <span className="badge">Doppelte Wertung</span> : null}
                      <BonusRewardStatus projection={projection} />
                      <h2>{projection.question.prompt}</h2>
                      <RoomImage
                        roomId={roomId}
                        token={session.token}
                        mediaId={projection.question.media.imageMediaId}
                        className={`question-media question-media--${projection.question.media.imageSize ?? "normal"}`}
                      />
                      <div className="answer-grid">
                        {projection.question.answers.map((answer) => {
                          const mark = projection.answerMarks.find((entry) => entry.optionId === answer.optionId);
                          return (
                            <div
                              className={`answer-card answer-card--host ${
                                "isCorrect" in answer && answer.isCorrect ? "answer-card--correct" : ""
                              } ${answerPresentationClasses(
                                answer.optionId,
                                projection.hiddenOptionIds,
                                projection.answerMarks,
                                projection.solutionOptionIds,
                                projection.fiftyFiftyAnimation,
                                fiftyFiftyComplete,
                              )}`}
                              key={answer.optionId}
                            >
                              <strong>{answer.text}</strong>
                              <AnswerMarkControl
                                mark={mark}
                                scoreTargets={manualScoreTargets}
                                onChange={(value) =>
                                  command({
                                    type: "SET_ANSWER_MARK",
                                    optionId: answer.optionId,
                                    ...value,
                                  })
                                }
                                onManualSolve={(target, visibility) =>
                                  command({
                                    type: "MANUAL_SOLVE",
                                    optionId: answer.optionId,
                                    target: target?.type,
                                    targetId: target?.id,
                                    ...visibility,
                                  })
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                      {"adminNotes" in projection.question && projection.question.adminNotes ? (
                        <div className="admin-note">Notiz: {projection.question.adminNotes}</div>
                      ) : null}
                    </>
                  ) : (
                    <div className="empty-state">Lobby ist geöffnet.</div>
                  )}
                  {projection.status !== "running" && projection.status !== "ended" ? (
                    <button
                      className="button button--accent"
                      onClick={() => command({ type: "START_GAME" })}
                    >
                      Quiz starten
                    </button>
                  ) : null}
                  <QuestionToolbar
                    projection={projection}
                    fiftyFiftyComplete={fiftyFiftyComplete}
                    command={command}
                    onPrevious={previousQuestion}
                    onReset={resetQuestion}
                  />
                  <div className={`buzzer-result ${buzzerWinner ? "buzzer-result--winner" : ""}`}>
                    {buzzerWinner
                      ? `${buzzerWinner.displayName} hat gebuzzert`
                      : projection.buzzer.status === "open"
                        ? "Buzzer aktiv"
                        : "Buzzer gesperrt"}
                  </div>
                  {projection.jokerAnnouncement ? (
                    <div className="joker-announcement">{projection.jokerAnnouncement.message}</div>
                  ) : null}
                </div>
              </article>

              {projection.jokerRequests?.some((request) => request.status === "pending") ? (
                <article className="card">
                  <div className="section__heading">
                    <h2>Joker-Anfragen</h2>
                  </div>
                  <div className="submission-list">
                    {projection.jokerRequests
                      .filter((request) => request.status === "pending")
                      .map((request) => {
                        const player = projection.players.find((entry) => entry.playerId === request.playerId);
                        const joker = projection.jokers.find((entry) => entry.jokerId === request.jokerId);
                        const validTargets = projection.players.filter(
                          (entry) => entry.status === "active" && entry.playerId !== request.playerId,
                        );
                        const targetPlayerId = jokerTargets[request.requestId] ?? validTargets[0]?.playerId;
                        return (
                          <div className="submission-row" key={request.requestId}>
                            <div>
                              <strong>{player?.displayName ?? "Teilnehmer"}</strong>
                              <span>{joker?.name ?? "Joker"}</span>
                            </div>
                            <div className="score-controls">
                              {joker?.type === "pass-question" ? (
                                <select
                                  aria-label="Zielspieler"
                                  value={targetPlayerId ?? ""}
                                  onChange={(event) =>
                                    setJokerTargets((current) => ({
                                      ...current,
                                      [request.requestId]: event.target.value,
                                    }))
                                  }
                                >
                                  {validTargets.map((entry) => (
                                    <option key={entry.playerId} value={entry.playerId}>
                                      {entry.displayName}
                                    </option>
                                  ))}
                                </select>
                              ) : null}
                              <button
                                className="button button--small button--accent"
                                disabled={joker?.type === "pass-question" && !targetPlayerId}
                                onClick={() =>
                                  command({
                                    type: "REVIEW_JOKER_REQUEST",
                                    requestId: request.requestId,
                                    accepted: true,
                                    targetPlayerId,
                                  })
                                }
                              >
                                Annehmen
                              </button>
                              <button
                                className="button button--small button--ghost"
                                onClick={() =>
                                  command({
                                    type: "REVIEW_JOKER_REQUEST",
                                    requestId: request.requestId,
                                    accepted: false,
                                  })
                                }
                              >
                                Ablehnen
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </article>
              ) : null}

              <article className="card submission-overview">
                <div className="section__heading">
                  <div>
                    <span className="eyebrow">Aktuelle Frage</span>
                    <h2>Teilnehmerantworten</h2>
                  </div>
                  <label className="room-mode-toggle">
                    <input
                      type="checkbox"
                      checked={!projection.participantAnswersEnabled}
                      onChange={(event) =>
                        command({ type: "SET_PARTICIPANT_ANSWERS", enabled: !event.target.checked })
                      }
                    />
                    Stream-only / Host bewertet selbst
                  </label>
                </div>
                {!projection.participantAnswersEnabled ? (
                  <div className="empty-state">
                    Teilnehmerantworten sind deaktiviert. Mit „Richtig werten“ vergibst du die Wertung; weitere
                    Varianten und Sichtbarkeiten findest du im Drei-Punkte-Menü.
                  </div>
                ) : !projection.submissions?.length ? (
                  <div className="empty-state">Noch keine Antworten abgegeben.</div>
                ) : (
                  <div className="submission-list">
                    {projection.submissions
                      .slice()
                      .sort((left, right) => left.receivedAt.localeCompare(right.receivedAt))
                      .map((submission, index) => {
                        const player = projection.players.find((entry) => entry.playerId === submission.playerId);
                        const team = projection.teams.find((entry) => entry.teamId === player?.teamId);
                        return (
                          <div className={`submission-row submission-row--${submission.evaluation}`} key={submission.submissionId}>
                            <div>
                              <strong>
                                {index + 1}. {player?.displayName ?? "Unbekannt"}
                                {team ? ` · ${team.name}` : ""}
                              </strong>
                              <span className="submission-answer">{submissionText(projection, submission)}</span>
                              <small className="muted">
                                {new Date(submission.receivedAt).toLocaleTimeString("de-DE")} ·{" "}
                                {evaluationLabels[submission.evaluation]}
                                {submission.suggestedEvaluation && submission.evaluation === "pending"
                                  ? ` · Vorschlag: ${evaluationLabels[submission.suggestedEvaluation]}`
                                  : ""}
                              </small>
                              {submission.effectSummary ? <small>{submission.effectSummary}</small> : null}
                            </div>
                            <div className="score-controls">
                              {submission.evaluation === "pending" ? (
                                <>
                                  <button
                                    className="button button--small button--accent"
                                    onClick={() =>
                                      command({
                                        type: "REVIEW_SUBMISSION",
                                        submissionId: submission.submissionId,
                                        evaluation: "correct",
                                      })
                                    }
                                  >
                                    Richtig
                                  </button>
                                  <button
                                    className="button button--small button--ghost"
                                    onClick={() =>
                                      command({
                                        type: "REVIEW_SUBMISSION",
                                        submissionId: submission.submissionId,
                                        evaluation: "incorrect",
                                      })
                                    }
                                  >
                                    Falsch
                                  </button>
                                  <button
                                    className="button button--small button--ghost"
                                    onClick={() =>
                                      command({
                                        type: "REVIEW_SUBMISSION",
                                        submissionId: submission.submissionId,
                                        evaluation: "partial",
                                      })
                                    }
                                  >
                                    Teilweise/Manuell
                                  </button>
                                </>
                              ) : null}
                              <button
                                className="button button--small button--ghost"
                                onClick={() =>
                                  command({
                                    type: "SET_SUBMISSION_VISIBILITY",
                                    submissionId: submission.submissionId,
                                    releasedToStream: !submission.releasedToStream,
                                  })
                                }
                              >
                                {submission.releasedToStream ? "Im Stream verbergen" : "Für Stream freigeben"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </article>

              <article className="card">
                <div className="section__heading">
                  <h2>Spieler</h2>
                  <span className="muted">{projection.players.length} im Raum</span>
                </div>
                <div className="player-list">
                  {projection.players.map((player) => (
                    <div className={`player-row player-row--${player.status}`} key={player.playerId}>
                      <div>
                        <strong>{player.displayName}</strong>
                        <span className="muted">
                          {player.status} ·{" "}
                          <ScoreStatus
                            projection={projection}
                            score={player.score}
                            rankProgress={player.rankProgress}
                            roomId={roomId}
                            token={session.token}
                          />
                          {projection.usesLives ? " · " : null}
                          <LivesStatus projection={projection} lives={player.lives} />
                        </span>
                        <JokerStatus
                          jokers={projection.jokers}
                          counts={player.jokerCounts}
                          roomId={roomId}
                          token={session.token}
                        />
                      </div>
                      <select
                        value={player.teamId ?? ""}
                        onChange={(event) =>
                          command({ type: "ASSIGN_PLAYER", playerId: player.playerId, teamId: event.target.value || undefined })
                        }
                      >
                        <option value="">Kein Team</option>
                        {projection.teams.map((team) => (
                          <option key={team.teamId} value={team.teamId}>
                            {team.name}
                          </option>
                        ))}
                      </select>
                      <div className="score-controls">
                        <button
                          className="button button--small button--accent"
                          disabled={!projection.question}
                          onClick={() =>
                            command({ type: "AWARD_QUESTION", target: "player", targetId: player.playerId, correct: true })
                          }
                        >
                          Richtig
                        </button>
                        <button
                          className="button button--small button--ghost"
                          disabled={!projection.question}
                          onClick={() =>
                            command({ type: "AWARD_QUESTION", target: "player", targetId: player.playerId, correct: false })
                          }
                        >
                          Falsch
                        </button>
                        <button
                          className="button button--small button--ghost"
                          onClick={() => adjustScoring("player", player.playerId, -1)}
                        >
                          -{scoringStep}
                        </button>
                        <button
                          className="button button--small button--primary"
                          onClick={() => adjustScoring("player", player.playerId, 1)}
                        >
                          +{scoringStep}
                        </button>
                        {projection.usesLives ? (
                          <>
                            <button
                              className="button button--small button--ghost"
                              onClick={() =>
                                command({ type: "ADD_LIFE", target: "player", targetId: player.playerId, delta: -1 })
                              }
                            >
                              -Leben
                            </button>
                            <button
                              className="button button--small button--ghost"
                              onClick={() =>
                                command({ type: "ADD_LIFE", target: "player", targetId: player.playerId, delta: 1 })
                              }
                            >
                              +Leben
                            </button>
                          </>
                        ) : null}
                        {projection.moduleId === "weakest-link" ? (
                          <button
                            className="button button--small button--ghost"
                            onClick={() =>
                              command({
                                type: player.status === "eliminated" ? "RESTORE_PLAYER" : "ELIMINATE_PLAYER",
                                playerId: player.playerId,
                              })
                            }
                          >
                            {player.status === "eliminated" ? "Zurückholen" : "Ausscheiden"}
                          </button>
                        ) : null}
                        {projection.jokers.map((joker) => (
                          <span className="joker-controls" key={joker.jokerId}>
                            <button
                              className="button button--small button--ghost"
                              onClick={() =>
                                command({
                                  type: "ADJUST_JOKER",
                                  target: "player",
                                  targetId: player.playerId,
                                  jokerId: joker.jokerId,
                                  delta: -1,
                                })
                              }
                            >
                              -{joker.icon || "J"}
                            </button>
                            <button
                              className="button button--small button--primary"
                              disabled={joker.type === "pass-question"}
                              onClick={() =>
                                command({
                                  type: "USE_JOKER",
                                  target: "player",
                                  targetId: player.playerId,
                                  jokerId: joker.jokerId,
                                })
                              }
                            >
                              {joker.type === "pass-question" ? "Über Teilnehmer-Anfrage" : `${joker.name} einsetzen`}
                            </button>
                            <button
                              className="button button--small button--ghost"
                              onClick={() =>
                                command({
                                  type: "ADJUST_JOKER",
                                  target: "player",
                                  targetId: player.playerId,
                                  jokerId: joker.jokerId,
                                  delta: 1,
                                })
                              }
                            >
                              +{joker.icon || "J"}
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </section>

            <aside className="control-side">
              <section className="card">
                <h2>Teams</h2>
                <div className="inline-form">
                  <input value={teamName} onChange={(event) => setTeamName(event.target.value)} placeholder="Teamname" />
                  <input type="color" value={teamColor} onChange={(event) => setTeamColor(event.target.value)} />
                  <input
                    aria-label="Teamlogo"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) => setTeamLogo(event.target.files?.[0])}
                  />
                  <button
                    className="button button--accent"
                    disabled={!teamName.trim()}
                    onClick={() => void createTeam()}
                  >
                    Erstellen
                  </button>
                </div>
                <div className="team-list">
                  {projection.teams.map((team) => (
                    <TeamControlCard
                      key={team.teamId}
                      color={team.color}
                      header={
                        <>
                        <RoomImage
                          roomId={roomId}
                          token={session.token}
                          mediaId={team.logoMediaId}
                          className="team-logo"
                        />
                        <div className="team-card__identity">
                          <input
                            aria-label={`Name von ${team.name}`}
                            defaultValue={team.name}
                            onBlur={(event) =>
                              command({ type: "UPDATE_TEAM", teamId: team.teamId, name: event.target.value })
                            }
                          />
                          <div className="team-card__stats">
                            <ScoreStatus
                              projection={projection}
                              score={team.score}
                              rankProgress={team.rankProgress}
                              roomId={roomId}
                              token={session.token}
                            />
                            <LivesStatus projection={projection} lives={team.lives} />
                            <JokerStatus
                              jokers={projection.jokers}
                              counts={team.jokerCounts}
                              roomId={roomId}
                              token={session.token}
                            />
                          </div>
                        </div>
                        <TeamColorControl
                          teamName={team.name}
                          value={team.color}
                          onCommit={(color) => command({ type: "UPDATE_TEAM", teamId: team.teamId, color })}
                        />
                        </>
                      }
                    >
                        <button
                          className="button button--small button--ghost"
                          onClick={() => adjustScoring("team", team.teamId, -1)}
                        >
                          -{scoringStep}
                        </button>
                        <button
                          className="button button--small button--primary"
                          onClick={() => adjustScoring("team", team.teamId, 1)}
                        >
                          +{scoringStep}
                        </button>
                        {projection.usesLives ? (
                          <>
                            <button
                              className="button button--small button--ghost"
                              onClick={() =>
                                command({ type: "ADD_LIFE", target: "team", targetId: team.teamId, delta: -1 })
                              }
                            >
                              -Leben
                            </button>
                            <button
                              className="button button--small button--ghost"
                              onClick={() =>
                                command({ type: "ADD_LIFE", target: "team", targetId: team.teamId, delta: 1 })
                              }
                            >
                              +Leben
                            </button>
                          </>
                        ) : null}
                        {projection.jokers.map((joker) => (
                          <span className="joker-controls" key={joker.jokerId}>
                            <button
                              className="button button--small button--ghost"
                              onClick={() =>
                                command({
                                  type: "ADJUST_JOKER",
                                  target: "team",
                                  targetId: team.teamId,
                                  jokerId: joker.jokerId,
                                  delta: -1,
                                })
                              }
                            >
                              -{joker.icon || "J"}
                            </button>
                            <button
                              className="button button--small button--primary"
                              disabled={joker.type === "pass-question"}
                              onClick={() =>
                                command({
                                  type: "USE_JOKER",
                                  target: "team",
                                  targetId: team.teamId,
                                  jokerId: joker.jokerId,
                                })
                              }
                            >
                              {joker.type === "pass-question" ? "Nur Spieler-Anfrage" : joker.name}
                            </button>
                            <button
                              className="button button--small button--ghost"
                              onClick={() =>
                                command({
                                  type: "ADJUST_JOKER",
                                  target: "team",
                                  targetId: team.teamId,
                                  jokerId: joker.jokerId,
                                  delta: 1,
                                })
                              }
                            >
                              +{joker.icon || "J"}
                            </button>
                          </span>
                        ))}
                    </TeamControlCard>
                  ))}
                </div>
              </section>
              <section className="card event-log">
                <h2>Eventlog</h2>
                {projection.eventLog?.slice(-100).reverse().map((event) => (
                  <div className="event-log__entry" key={event.eventId}>
                    <span>r{event.revision}</span>
                    <p>{event.summary}</p>
                  </div>
                ))}
              </section>
            </aside>
          </div>
        </>
      )}
    </main>
  );
}

function EndScreen({ projection }: { projection: NonNullable<ReturnType<typeof useRoomConnection>["projection"]> }) {
  const entries = [...(projection.teams.length ? projection.teams : projection.players)].sort((left, right) =>
    projection.scoringMode === "rank-progress"
      ? right.rankProgress - left.rankProgress
      : right.score - left.score,
  );
  return (
    <div className="end-screen">
      <span className="eyebrow">Finale</span>
      <h2>Quiz beendet</h2>
      {entries.map((entry, index) => (
        <div className="end-screen__entry" key={"teamId" in entry ? entry.teamId : entry.playerId}>
          <span>{index + 1}</span>
          <strong>{"name" in entry ? entry.name : entry.displayName}</strong>
          <b>
            <ScoreStatus projection={projection} score={entry.score} rankProgress={entry.rankProgress} />
          </b>
        </div>
      ))}
    </div>
  );
}
